'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from 'recharts';

const MODELS = [
  { id: 'random_forest', name: 'Random Forest', version: '2.1.0', accuracy: 74.31, brier: 0.1872, logLoss: 0.4568, roi: 12.34, status: 'best', color: '#00D4FF' },
  { id: 'xgboost', name: 'XGBoost', version: '1.8.0', accuracy: 72.27, brier: 0.1985, logLoss: 0.4789, roi: 9.87, status: 'active', color: '#FF6B35' },
  { id: 'lightgbm', name: 'LightGBM', version: '1.5.0', accuracy: 71.44, brier: 0.2035, logLoss: 0.4890, roi: 8.45, status: 'active', color: '#00C853' },
  { id: 'elo', name: 'Elo Dynamique', version: '3.0.0', accuracy: 69.54, brier: 0.2189, logLoss: 0.5123, roi: 6.23, status: 'active', color: '#FFB800' },
  { id: 'logistic_regression', name: 'Logistic Regression', version: '1.2.0', accuracy: 68.24, brier: 0.2235, logLoss: 0.5235, roi: 4.56, status: 'active', color: '#A855F7' },
];

const ACCURACY_EVOLUTION = [
  { month: 'Oct', random_forest: 71.2, xgboost: 69.8, lightgbm: 68.9, elo: 67.1 },
  { month: 'Nov', random_forest: 72.8, xgboost: 70.9, lightgbm: 69.7, elo: 67.8 },
  { month: 'Dec', random_forest: 73.1, xgboost: 71.4, lightgbm: 70.2, elo: 68.2 },
  { month: 'Jan', random_forest: 73.9, xgboost: 71.8, lightgbm: 70.8, elo: 68.9 },
  { month: 'Feb', random_forest: 74.1, xgboost: 72.1, lightgbm: 71.1, elo: 69.3 },
  { month: 'Mar', random_forest: 74.3, xgboost: 72.3, lightgbm: 71.4, elo: 69.5 },
];

const RADAR_DATA = [
  { metric: 'Précision', random_forest: 92, xgboost: 89, lightgbm: 88 },
  { metric: 'Calibration', random_forest: 88, xgboost: 85, lightgbm: 84 },
  { metric: 'ROI', random_forest: 95, xgboost: 87, lightgbm: 82 },
  { metric: 'Vitesse', random_forest: 78, xgboost: 92, lightgbm: 95 },
  { metric: 'Stabilité', random_forest: 91, xgboost: 86, lightgbm: 85 },
  { metric: 'Conf. Haute', random_forest: 94, xgboost: 90, lightgbm: 88 },
];

const TOOLTIP_STYLE = {
  backgroundColor: '#111827',
  border: '1px solid #1E2D4A',
  borderRadius: '6px',
  color: '#E8EDF5',
  fontSize: '11px',
};

const SAMPLE_PREDICTIONS = [
  { home: 'TOR', away: 'BOS', homeProb: 62.3, awayProb: 37.7, homeXG: 3.12, awayXG: 2.34, confidence: 81.2, ci: '±8.4%', volatility: 'Low', model: 'Random Forest' },
  { home: 'EDM', away: 'VGK', homeProb: 56.8, awayProb: 43.2, homeXG: 2.87, awayXG: 2.54, confidence: 74.6, ci: '±11.2%', volatility: 'Medium', model: 'Random Forest' },
  { home: 'FLA', away: 'NYR', homeProb: 42.3, awayProb: 57.7, homeProb2: 42.3, homeXG: 2.21, awayXG: 2.98, confidence: 82.3, ci: '±7.8%', volatility: 'Low', model: 'Random Forest' },
  { home: 'COL', away: 'DAL', homeProb: 67.9, awayProb: 32.1, homeXG: 3.78, awayXG: 2.54, confidence: 85.7, ci: '±6.2%', volatility: 'Very Low', model: 'Random Forest' },
];

const VOLATILITY_COLOR: Record<string, string> = {
  'Very Low': 'text-positive',
  'Low': 'text-positive',
  'Medium': 'text-warning',
  'High': 'text-negative',
};

export default function MLEngineClient() {
  const [selectedModel, setSelectedModel] = useState('random_forest');
  const [training, setTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);

  const handleTrain = () => {
    setTraining(true);
    setTrainingProgress(0);
    const interval = setInterval(() => {
      setTrainingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTraining(false);
          return 100;
        }
        return prev + Math.random() * 12;
      });
    }, 400);
  };

  const activeModel = MODELS.find(m => m.id === selectedModel) || MODELS[0];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">ML Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Modèles prédictifs comparatifs — Auto-sélection du meilleur modèle
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="status-badge bg-positive-subtle text-positive border border-positive/20 font-mono-data">
            Best: Random Forest v2.1.0
          </span>
          <button onClick={handleTrain} disabled={training} className="btn-primary flex items-center gap-2">
            <Icon name="CpuChipIcon" size={14} />
            {training ? `Entraînement... ${Math.round(trainingProgress)}%` : 'Entraîner modèles'}
          </button>
        </div>
      </div>

      {/* Training progress */}
      {training && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Entraînement en cours — Tous les modèles</span>
            <span className="text-sm font-mono-data text-primary">{Math.round(trainingProgress)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${trainingProgress}%` }} />
          </div>
          <div className="grid grid-cols-5 gap-2 mt-3">
            {MODELS.map((m, i) => (
              <div key={m.id} className={`text-2xs font-mono-data text-center ${trainingProgress > i * 20 ? 'text-positive' : 'text-muted-foreground'}`}>
                {trainingProgress > i * 20 ? '✓' : '○'} {m.name.split(' ')[0]}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Model cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {MODELS.map((model) => (
          <button
            key={model.id}
            onClick={() => setSelectedModel(model.id)}
            className={`card p-3 text-left transition-all duration-150 ${selectedModel === model.id ? 'border-primary/50 bg-primary/5' : 'hover:border-border/80'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xs font-mono-data text-muted-foreground">v{model.version}</span>
              {model.status === 'best' && (
                <span className="status-badge bg-positive-subtle text-positive text-2xs border border-positive/20">BEST</span>
              )}
            </div>
            <p className="text-xs font-semibold text-foreground mb-1">{model.name}</p>
            <p className="text-xl font-bold font-mono-data" style={{ color: model.color }}>{model.accuracy}%</p>
            <p className="text-2xs text-muted-foreground mt-1">ROI: +{model.roi}%</p>
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Accuracy evolution */}
        <div className="lg:col-span-2 card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="ArrowTrendingUpIcon" size={14} className="text-primary" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Évolution précision — Saison 2024-25</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={ACCURACY_EVOLUTION}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis domain={[65, 78]} tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`]} />
              <Legend wrapperStyle={{ fontSize: '10px', color: '#6B7A99' }} />
              <Line type="monotone" dataKey="random_forest" name="Random Forest" stroke="#00D4FF" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="xgboost" name="XGBoost" stroke="#FF6B35" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="lightgbm" name="LightGBM" stroke="#00C853" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="elo" name="Elo" stroke="#FFB800" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Radar */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="ChartBarIcon" size={14} className="text-accent" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Comparaison Top 3</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={RADAR_DATA}>
              <PolarGrid stroke="#1E2D4A" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: '#6B7A99', fontSize: 9 }} />
              <Radar name="Random Forest" dataKey="random_forest" stroke="#00D4FF" fill="#00D4FF" fillOpacity={0.15} strokeWidth={2} />
              <Radar name="XGBoost" dataKey="xgboost" stroke="#FF6B35" fill="#FF6B35" fillOpacity={0.1} strokeWidth={1.5} />
              <Radar name="LightGBM" dataKey="lightgbm" stroke="#00C853" fill="#00C853" fillOpacity={0.1} strokeWidth={1.5} />
              <Legend wrapperStyle={{ fontSize: '10px', color: '#6B7A99' }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sample predictions */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="BoltIcon" size={14} className="text-primary" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Projections — {activeModel.name}</span>
          </div>
          <span className="text-2xs font-mono-data text-muted-foreground">Saison 2025-26 · Matchs du jour</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="table-header">Match</th>
                <th className="table-header">Prob. Domicile</th>
                <th className="table-header">xGoals</th>
                <th className="table-header">Confiance</th>
                <th className="table-header">IC 95%</th>
                <th className="table-header">Volatilité</th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_PREDICTIONS.map((pred, i) => (
                <tr key={i} className="border-b border-border/50 row-hover">
                  <td className="table-cell">
                    <span className="font-semibold font-mono-data text-foreground">{pred.home}</span>
                    <span className="text-muted-foreground mx-1">vs</span>
                    <span className="font-mono-data text-muted-foreground">{pred.away}</span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-muted rounded-full h-1.5">
                        <div className="bg-primary h-1.5 rounded-full" style={{ width: `${pred.homeProb}%` }} />
                      </div>
                      <span className="font-mono-data text-primary text-xs">{pred.homeProb}%</span>
                    </div>
                  </td>
                  <td className="table-cell font-mono-data text-xs">
                    <span className="text-positive">{pred.homeXG}</span>
                    <span className="text-muted-foreground mx-1">—</span>
                    <span className="text-accent">{pred.awayXG}</span>
                  </td>
                  <td className="table-cell">
                    <span className={`font-mono-data text-xs font-semibold ${pred.confidence >= 80 ? 'text-positive' : pred.confidence >= 70 ? 'text-warning' : 'text-negative'}`}>
                      {pred.confidence}%
                    </span>
                  </td>
                  <td className="table-cell font-mono-data text-xs text-muted-foreground">{pred.ci}</td>
                  <td className="table-cell">
                    <span className={`text-xs font-medium ${VOLATILITY_COLOR[pred.volatility]}`}>{pred.volatility}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Model versioning */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="ArchiveBoxIcon" size={14} className="text-warning" />
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Versioning des modèles</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {MODELS.map((model) => (
            <div key={model.id} className="bg-muted/50 rounded-lg p-3 border border-border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-foreground">{model.name.split(' ')[0]}</span>
                <span className="text-2xs font-mono-data text-muted-foreground">v{model.version}</span>
              </div>
              <p className="text-lg font-bold font-mono-data" style={{ color: model.color }}>{model.accuracy}%</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-2xs">
                  <span className="text-muted-foreground">Brier</span>
                  <span className="font-mono-data text-foreground">{model.brier}</span>
                </div>
                <div className="flex justify-between text-2xs">
                  <span className="text-muted-foreground">Log Loss</span>
                  <span className="font-mono-data text-foreground">{model.logLoss}</span>
                </div>
                <div className="flex justify-between text-2xs">
                  <span className="text-muted-foreground">ROI</span>
                  <span className="font-mono-data text-positive">+{model.roi}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
